import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  ArrowLeft, Heart, Share2, MessageCircle, MapPin, Clock, Eye, 
  Star, Gift, Users, Compass, BookOpen, Palette, Briefcase,
  Send, Loader2, CheckCircle, Copy, ExternalLink, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { HeartAlchemyIcon, LotusIcon, SpiralIcon, CrystalIcon, InfinityAlchemyIcon } from './icons/AlchemyIcons';
import PremiumFooter from './PremiumFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category configurations
const CATEGORY_CONFIG = {
  coaching: { icon: Compass, label: 'Coaching & Guidance', color: 'amber' },
  mentoring: { icon: Users, label: 'Mentoring', color: 'teal' },
  healing: { icon: HeartAlchemyIcon, label: 'Healing & Wellness', color: 'rose' },
  teaching: { icon: BookOpen, label: 'Teaching & Education', color: 'blue' },
  creative: { icon: Palette, label: 'Creative & Art', color: 'violet' },
  professional: { icon: Briefcase, label: 'Professional Skills', color: 'slate' },
  spiritual: { icon: LotusIcon, label: 'Spiritual & Mindfulness', color: 'purple' },
  nvc: { icon: MessageCircle, label: 'NVC & Communication', color: 'emerald' },
  other: { icon: Gift, label: 'Other Gifts', color: 'orange' }
};

const GRADIENTS = {
  amber: 'from-amber-600 via-amber-500 to-orange-600',
  teal: 'from-teal-600 via-teal-500 to-emerald-600',
  rose: 'from-rose-600 via-rose-500 to-pink-600',
  blue: 'from-blue-600 via-blue-500 to-indigo-600',
  violet: 'from-violet-600 via-violet-500 to-purple-600',
  slate: 'from-slate-600 via-slate-500 to-gray-600',
  purple: 'from-purple-600 via-purple-500 to-violet-600',
  emerald: 'from-emerald-600 via-emerald-500 to-teal-600',
  orange: 'from-orange-600 via-orange-500 to-amber-600'
};

const CREDIBILITY_LEVELS = {
  newcomer: { label: 'Newcomer', description: 'Just starting their giving journey' },
  emerging_giver: { label: 'Emerging Giver', description: 'Building trust in the community' },
  active_contributor: { label: 'Active Contributor', description: 'Regularly sharing gifts' },
  trusted_giver: { label: 'Trusted Giver', description: 'Highly trusted community member' },
  community_pillar: { label: 'Community Pillar', description: 'A cornerstone of generosity' }
};

function getCredibilityLevel(score) {
  if (score >= 500) return 'community_pillar';
  if (score >= 200) return 'trusted_giver';
  if (score >= 100) return 'active_contributor';
  if (score >= 25) return 'emerging_giver';
  return 'newcomer';
}

export default function OfferingPage() {
  const { offeringId } = useParams();
  const navigate = useNavigate();
  
  const [offering, setOffering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [hasExpressedGratitude, setHasExpressedGratitude] = useState(false);
  
  const userId = localStorage.getItem('workbook_user_id');
  const userName = localStorage.getItem('workbook_user_name');

  useEffect(() => {
    fetchOffering();
  }, [offeringId]);

  const fetchOffering = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/offerings/${offeringId}`);
      if (response.ok) {
        const data = await response.json();
        setOffering(data);
      } else {
        toast.error('Offering not found');
        navigate('/forum');
      }
    } catch (error) {
      console.error('Failed to fetch offering:', error);
      toast.error('Failed to load offering');
    } finally {
      setLoading(false);
    }
  };

  const handleContact = async () => {
    if (!userId) {
      toast.error('Please log in to contact the giver');
      navigate('/login');
      return;
    }

    if (!contactMessage.trim()) {
      toast.error('Please write a message');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/offerings/${offeringId}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({ message: contactMessage })
      });

      if (response.ok) {
        toast.success('Message sent! Check your messages for their reply.');
        setShowContactDialog(false);
        setContactMessage('');
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleGratitude = async () => {
    if (!userId) {
      toast.error('Please log in to express gratitude');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/offerings/${offeringId}/gratitude`, {
        method: 'POST',
        headers: {
          'X-User-Id': userId,
          'X-User-Name': userName
        }
      });

      if (response.ok) {
        setHasExpressedGratitude(true);
        setOffering(prev => ({
          ...prev,
          gratitude_count: (prev.gratitude_count || 0) + 1
        }));
        toast.success('Your gratitude has been shared! 💚');
      } else {
        const data = await response.json();
        toast.info(data.detail || 'Already expressed gratitude');
      }
    } catch (error) {
      toast.error('Failed to express gratitude');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: offering.title,
          text: `${offering.giver_name} is offering: ${offering.title}`,
          url: shareUrl
        });
      } catch (err) {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!offering) {
    return null;
  }

  const color = offering.cover_color || 'amber';
  const gradient = GRADIENTS[color] || GRADIENTS.amber;
  const categoryConfig = CATEGORY_CONFIG[offering.category] || CATEGORY_CONFIG.other;
  const CategoryIcon = categoryConfig.icon;
  const credibilityLevel = getCredibilityLevel(offering.giver_credibility || 0);
  const credibilityInfo = CREDIBILITY_LEVELS[credibilityLevel];
  const isOwnOffering = userId === offering.giver_id;

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Hero Header */}
      <header className={`relative bg-gradient-to-br ${gradient} overflow-hidden`}>
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <SpiralIcon className="absolute top-10 right-10 w-64 h-64" />
          <InfinityAlchemyIcon className="absolute bottom-10 left-10 w-48 h-48" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-8 sm:py-12">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/forum')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Community</span>
          </button>
          
          {/* Category Badge */}
          <Badge className="bg-white/20 text-white border-white/30 mb-4">
            <CategoryIcon className="w-4 h-4 mr-2" />
            {categoryConfig.label}
          </Badge>
          
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            {offering.title}
          </h1>
          
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
            {offering.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {offering.location === 'remote' ? 'Available Remotely' : offering.area || 'Local'}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {offering.availability === 'ongoing' ? 'Ongoing' : 
               offering.availability === 'limited' ? 'Limited Availability' : 'One-time'}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {offering.views || 0} views
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Description */}
            <Card className="bg-stone-900/50 border-stone-800">
              <CardContent className="p-6">
                <h2 className="text-lg font-medium text-amber-300 mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  About This Gift
                </h2>
                <p className="text-stone-300 leading-relaxed whitespace-pre-wrap">
                  {offering.description}
                </p>
              </CardContent>
            </Card>
            
            {/* Tags */}
            {offering.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {offering.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="border-stone-700 text-stone-400">
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {!isOwnOffering && (
                <Button
                  onClick={() => setShowContactDialog(true)}
                  className={`bg-gradient-to-r ${gradient} text-white hover:opacity-90`}
                  data-testid="contact-giver-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contact {offering.giver_name?.split(' ')[0]}
                </Button>
              )}
              
              <Button
                onClick={handleGratitude}
                disabled={hasExpressedGratitude || isOwnOffering}
                variant="outline"
                className={`border-rose-500/50 text-rose-400 hover:bg-rose-500/10 ${hasExpressedGratitude ? 'opacity-50' : ''}`}
                data-testid="gratitude-btn"
              >
                <Heart className={`w-4 h-4 mr-2 ${hasExpressedGratitude ? 'fill-current' : ''}`} />
                {hasExpressedGratitude ? 'Gratitude Sent!' : `Express Gratitude (${offering.gratitude_count || 0})`}
              </Button>
              
              <Button
                onClick={handleShare}
                variant="outline"
                className="border-stone-700 text-stone-400 hover:bg-stone-800"
                data-testid="share-offering-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
          
          {/* Right Column - Giver Info */}
          <div className="space-y-6">
            {/* Giver Card */}
            <Card className="bg-stone-900/50 border-stone-800">
              <CardContent className="p-6 text-center">
                <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-2xl font-medium mx-auto mb-4 border-2 border-white/20`}>
                  {offering.giver_name?.charAt(0).toUpperCase()}
                </div>
                
                <h3 className="text-xl font-medium text-white mb-1">{offering.giver_name}</h3>
                
                <Badge className={`mb-4 ${
                  credibilityLevel === 'community_pillar' ? 'bg-gradient-to-r from-amber-500/20 to-rose-500/20 text-amber-300 border-amber-500/30' :
                  credibilityLevel === 'trusted_giver' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                  credibilityLevel === 'active_contributor' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                  credibilityLevel === 'emerging_giver' ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' :
                  'bg-stone-500/20 text-stone-400 border-stone-500/30'
                }`}>
                  <Star className="w-3 h-3 mr-1" />
                  {credibilityInfo.label}
                </Badge>
                
                <p className="text-xs text-stone-500 mb-4">{credibilityInfo.description}</p>
                
                {/* Credibility Stats */}
                {offering.giver_credibility_details && (
                  <div className="grid grid-cols-3 gap-2 text-center pt-4 border-t border-stone-800">
                    <div>
                      <p className="text-lg font-medium text-amber-400">
                        {offering.giver_credibility_details.gifts_offered || 0}
                      </p>
                      <p className="text-[10px] text-stone-500">Gifts Offered</p>
                    </div>
                    <div>
                      <p className="text-lg font-medium text-rose-400">
                        {offering.giver_credibility_details.gratitude_received || 0}
                      </p>
                      <p className="text-[10px] text-stone-500">Gratitude</p>
                    </div>
                    <div>
                      <p className="text-lg font-medium text-teal-400">
                        {offering.giver_credibility_details.credibility_score || 0}
                      </p>
                      <p className="text-[10px] text-stone-500">Credibility</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Share Card */}
            <Card className="bg-gradient-to-br from-stone-900/50 to-stone-800/30 border-stone-700">
              <CardContent className="p-4">
                <p className="text-sm text-stone-400 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  Share this gift with others
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleShare}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-stone-700 text-stone-300 hover:bg-stone-800"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-light flex items-center gap-2 text-amber-300" style={{ fontFamily: 'Playfair Display, serif' }}>
              <MessageCircle className="w-5 h-5" />
              Contact {offering.giver_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-stone-800/50 rounded-lg border border-stone-700">
              <p className="text-xs text-stone-500 mb-1">About:</p>
              <p className="text-sm text-white">{offering.title}</p>
            </div>
            
            <Textarea
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              placeholder="Share why you're interested in this gift and how it might help you..."
              className="bg-stone-800 border-stone-700 text-white min-h-[120px]"
              data-testid="contact-message-input"
            />
            
            <p className="text-xs text-stone-500">
              💚 Remember: This is a gift freely offered. Approach with gratitude and respect.
            </p>
            
            <Button
              onClick={handleContact}
              disabled={!contactMessage.trim() || sending}
              className={`w-full bg-gradient-to-r ${gradient} text-white hover:opacity-90`}
              data-testid="send-contact-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PremiumFooter />
    </div>
  );
}
